/*
Copyright (C) 2026  Integrated Tech9labs Pvt. Ltd.
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import * as React from "react";
import styled from "styled-components";
import type { LucideIcon } from "lucide-react";

import { ThemePalette } from "@src/components/Theme";

/*
 * The Tech9IQ icon tile (AWS Management / Ops Console service cards): a Lucide line icon on a
 * brand-50 rounded square. Every list item, card and menu in Swift uses this instead of the old
 * illustrations, so icons read the same across the Tech9IQ modules.
 *   tone: "brand" (default, slate), "info" (transfers), "success", "warning", "danger", "neutral"
 */
/*
 * One colour for every icon (UI spec, item 22): the portal's brand tile whatever the tone, so an
 * overview never shows a rainbow of icons. Status belongs in pills and badges, not in icons. The
 * tone prop stays for callers; the coloured tiles it used to pick are kept below for reference.
 */
const BRAND = { bg: ThemePalette.brandSoft, fg: ThemePalette.primary };
const TONES = {
  brand: BRAND,
  info: BRAND,
  success: BRAND,
  warning: BRAND,
  danger: BRAND,
  neutral: BRAND,
};
/*
const TONES = {
  brand: { bg: ThemePalette.brandSoft, fg: ThemePalette.primary },
  info: { bg: "var(--t9-info-50)", fg: "var(--t9-info-700)" },
  success: { bg: "var(--t9-success-50)", fg: "var(--t9-success-700)" },
  warning: { bg: "var(--t9-warning-50)", fg: "var(--t9-warning-700)" },
  danger: { bg: "var(--t9-danger-50)", fg: "var(--t9-danger-700)" },
  neutral: { bg: ThemePalette.grayscale[1], fg: ThemePalette.grayscale[4] },
};
*/
export type IconTileTone = keyof typeof TONES;

const Tile = styled.div<{ size: number; tone: IconTileTone }>`
  width: ${p => p.size}px;
  height: ${p => p.size}px;
  min-width: ${p => p.size}px;
  border-radius: ${p => (p.size >= 48 ? 10 : 6)}px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${p => TONES[p.tone].bg};
  color: ${p => TONES[p.tone].fg};
`;

type Props = {
  icon: LucideIcon;
  size?: number;
  tone?: IconTileTone;
  className?: string;
  title?: string;
};
const IconTile = ({ icon: Icon, size = 36, tone = "brand", className, title }: Props) => (
  <Tile size={size} tone={tone} className={className} title={title} aria-hidden={title ? undefined : true}>
    <Icon size={Math.round(size * 0.55)} strokeWidth={1.75} />
  </Tile>
);

export default IconTile;
